// Small pure helpers for reports. Money is integer ngwee: every sum here stays an integer.
import { shiftMonth } from './format'

/** Sum `amount` per key, keeping the order keys were first seen. */
export function groupSum<T>(rows: T[], key: (r: T) => string, amount: (r: T) => number): { key: string; ngwee: number }[] {
  const m = new Map<string, number>()
  for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + amount(r))
  return [...m.entries()].map(([k, ngwee]) => ({ key: k, ngwee }))
}

export const total = (xs: number[]): number => xs.reduce((s, n) => s + n, 0)

/** The quarter before `id` ('2026-Q1' -> '2025-Q4'). */
export function previousQuarter(id: string): string {
  const m = /^(\d{4})-Q([1-4])$/.exec(id)
  if (!m) throw new Error(`Invalid quarter id '${id}'`)
  const y = Number(m[1])
  const q = Number(m[2])
  return q === 1 ? `${y - 1}-Q4` : `${y}-Q${q - 1}`
}

/** `count` quarters ending at `from`, newest first: ('2026-Q4', 3) -> 2026-Q4, 2026-Q3, 2026-Q2. */
export function recentQuarters(from: string, count: number): string[] {
  const out: string[] = []
  let q = from
  for (let i = 0; i < count; i++) {
    out.push(q)
    q = previousQuarter(q)
  }
  return out
}

/** Percent of `part` in `whole`, as a whole number 0-100 (0 when there is no whole). */
export function percent(part: number, whole: number): number {
  if (whole <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((part / whole) * 100)))
}

export type Trend = { direction: 'up' | 'down' | 'same'; tone: 'good' | 'bad' | 'neutral' }

/**
 * How this quarter's figure moved against last quarter's, and whether that is good news.
 * `goodWhen` says which way is good: 'up' (money collected), 'down' (money sent but not yet
 * confirmed), or 'none' when neither way is better (spending, which is just a fact).
 */
export function trend(current: number, previous: number, goodWhen: 'up' | 'down' | 'none'): Trend {
  if (current === previous) return { direction: 'same', tone: 'neutral' }
  const direction = current > previous ? 'up' : 'down'
  return { direction, tone: goodWhen === 'none' ? 'neutral' : direction === goodWhen ? 'good' : 'bad' }
}

/** `count` months ending at `from`, newest first: ('2026-10', 3) -> 2026-10, 2026-09, 2026-08. */
export function recentMonths(from: string, count: number): string[] {
  const out: string[] = []
  let m = from
  for (let i = 0; i < count; i++) {
    out.push(m)
    m = shiftMonth(m, -1)
  }
  return out
}
